import { useLocation } from '@/contexts/LocationContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';
import { Product } from '@/features/inventory/types';

type CreateOrderInput = RouterInputs['purchaseOrders']['createOrder'];
type CreateOrderOutput = RouterOutputs['purchaseOrders']['createOrder'];
type UpdateDraftInput = RouterInputs['purchaseOrders']['updateDraft'];
type SubmitDraftInput = RouterInputs['purchaseOrders']['submitDraft'];
type DeleteDraftInput = RouterInputs['purchaseOrders']['deleteDraft'];

/**
 * Hook for purchase order submission and draft management
 * Handles creating orders, drafts, and submitting drafts
 */
export function useSubmitOrder() {
  const { selectedLocation } = useLocation();
  const { profile: _profile } = useAuth();
  const utils = trpc.useUtils();
  const resolveLocationId = (locationId?: string | null) => {
    const resolved = locationId ?? selectedLocation?.id ?? null;
    if (!resolved) throw new Error("No location selected");
    return resolved;
  };

  // CREATE ORDER (direct submit - not draft)
  const createOrderMutation = trpc.purchaseOrders.createOrder.useMutation({
    onSuccess: (result: CreateOrderOutput) => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();
      utils.inventory.getProducts.invalidate();

      const isTransfer = result.data.order_type === 'transfer';
      toast({
        title: isTransfer ? "Transfer Created" : "Order Created",
        description: isTransfer
          ? `Transfer ${result.data.po_number} created with ${result.data.total_items} items`
          : `Purchase order ${result.data.po_number} created with ${result.data.total_items} items for ${result.data.supplier_name}`,
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useSubmitOrder',
          operation: 'createOrder',
          location_id: selectedLocation?.id
        }
      });

      const errorMessage = error.message || "Unknown error occurred";
      const description = errorMessage.toLowerCase().includes('at least one item')
        ? "Please add at least one item to your order"
        : errorMessage;

      toast({
        title: "Error Creating Order",
        description,
        variant: "destructive",
      });
    }
  });

  // CREATE DRAFT ORDER
  const createDraftMutation = trpc.purchaseOrders.createOrder.useMutation({
    onSuccess: (result: CreateOrderOutput) => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();
      utils.inventory.getProducts.invalidate();

      toast({
        title: "Draft Order Saved",
        description: `Draft order ${result.data.po_number} saved with ${result.data.total_items} items${result.data.supplier_name ? ` for ${result.data.supplier_name}` : ''}`,
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useSubmitOrder',
          operation: 'createDraft',
          location_id: selectedLocation?.id
        },
        level: 'warning'
      });

      const errorMessage = error.message || "Unknown error occurred";
      const description = errorMessage.toLowerCase().includes('at least one item')
        ? "Please add at least one item to your draft order"
        : errorMessage;

      toast({
        title: "Error Creating Draft Order",
        description,
        variant: "destructive",
      });
    }
  });

  // UPDATE DRAFT ORDER
  const updateDraftMutation = trpc.purchaseOrders.updateDraft.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();
      utils.inventory.getProducts.invalidate();

      toast({
        title: "Order Updated",
        description: "Purchase order updated successfully",
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useSubmitOrder',
          operation: 'updateDraft',
          location_id: selectedLocation?.id
        }
      });

      const errorMessage = error.message || "Unknown error occurred";
      const description = errorMessage.toLowerCase().includes('at least one item')
        ? "Please ensure your draft has at least one item"
        : errorMessage;

      toast({
        title: "Error Updating Draft",
        description,
        variant: "destructive",
      });
    }
  });

  // SUBMIT DRAFT ORDER (convert draft to submitted)
  const submitDraftMutation = trpc.purchaseOrders.submitDraft.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();
      utils.inventory.getProducts.invalidate();

      toast({
        title: "Order Submitted",
        description: "Draft order submitted successfully",
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useSubmitOrder',
          operation: 'submitDraft',
          location_id: selectedLocation?.id
        }
      });

      const errorMessage = error.message || "Unknown error occurred";
      const description = errorMessage.toLowerCase().includes('without any items')
        ? "Cannot submit an order without any items. Please add items first."
        : errorMessage;

      toast({
        title: "Error Submitting Order",
        description,
        variant: "destructive",
      });
    }
  });

  // DELETE DRAFT ORDER
  const deleteDraftMutation = trpc.purchaseOrders.deleteDraft.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: "Draft Deleted",
        description: "Draft order deleted successfully",
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useSubmitOrder',
          operation: 'deleteDraft',
          location_id: selectedLocation?.id
        },
        level: 'warning'
      });

      toast({
        title: "Error Deleting Draft",
        description: error.message || "Unknown error occurred",
        variant: "destructive",
      });
    }
  });

  return {
    createOrder: async ({
      products,
      supplierId,
      supplierName,
      orderQuantities,
      title,
      description,
      etaDate,
      currency,
      exchangeRate,
      orderType,
      sourceLocationId,
    }: {
      products: Product[];
      supplierId?: string | null;
      supplierName: string;
      orderQuantities: Record<string, number>;
      title?: string;
      description?: string;
      etaDate?: string;
      currency?: string;
      exchangeRate?: number;
      orderType?: 'purchase' | 'transfer';
      sourceLocationId?: string;
    }) => {
      if (!selectedLocation) throw new Error("No location selected");

      const isTransfer = orderType === 'transfer';

      // Validation
      const itemsToOrder = products.filter(product => {
        const qty = orderQuantities[product.id];
        return qty !== undefined && qty > 0;
      });
      if (itemsToOrder.length === 0) {
        throw new Error("No items selected for order");
      }
      if (!isTransfer && !supplierName.trim()) {
        throw new Error("Supplier name is required");
      }

      // Backend calculates totalItems and submittedBy
      const payload: CreateOrderInput = {
        locationId: selectedLocation.id,
        supplierId: supplierId ?? undefined,
        supplierName: isTransfer ? '' : supplierName,
        title: title ?? undefined,
        description: description ?? undefined,
        etaDate: etaDate || undefined,
        status: 'submitted',
        currency: isTransfer ? undefined : (currency ?? undefined),
        exchangeRate: isTransfer ? undefined : (exchangeRate ?? undefined),
        orderType: orderType ?? undefined,
        sourceLocationId: isTransfer ? sourceLocationId : undefined,
        items: itemsToOrder.map(product => {
          const qty = orderQuantities[product.id];
          if (qty === undefined) throw new Error("Invalid quantity");
          return {
            skuId: product.id,
            orderedQuantity: qty
          };
        })
      };

      return await createOrderMutation.mutateAsync(payload);
    },
    createDraftOrder: async ({
      products,
      supplierId,
      supplierName,
      orderQuantities,
      title,
      description,
      etaDate,
      currency,
      exchangeRate,
      orderType,
      sourceLocationId,
    }: {
      products: Product[];
      supplierId?: string | null;
      supplierName: string;
      orderQuantities: Record<string, number>;
      title?: string;
      description?: string;
      etaDate?: string;
      currency?: string;
      exchangeRate?: number;
      orderType?: 'purchase' | 'transfer';
      sourceLocationId?: string;
    }) => {
      if (!selectedLocation) throw new Error("No location selected");

      const isTransfer = orderType === 'transfer';

      // Validation
      const itemsToOrder = products.filter(product => {
        const qty = orderQuantities[product.id];
        return qty !== undefined && qty > 0;
      });
      if (itemsToOrder.length === 0) {
        throw new Error("No items selected for draft order");
      }

      // Backend calculates totalItems and submittedBy
      const payload: CreateOrderInput = {
        locationId: selectedLocation.id,
        supplierId: supplierId ?? undefined,
        supplierName: isTransfer ? '' : supplierName,
        title: title ?? undefined,
        description: description ?? undefined,
        etaDate: etaDate || undefined,
        status: 'draft',
        currency: isTransfer ? undefined : (currency ?? undefined),
        exchangeRate: isTransfer ? undefined : (exchangeRate ?? undefined),
        orderType: orderType ?? undefined,
        sourceLocationId: isTransfer ? sourceLocationId : undefined,
        items: itemsToOrder.map(product => {
          const qty = orderQuantities[product.id];
          if (qty === undefined) throw new Error("Invalid quantity");
          return {
            skuId: product.id,
            orderedQuantity: qty
          };
        })
      };

      return await createDraftMutation.mutateAsync(payload);
    },
    updateDraftOrder: async ({
      orderId,
      supplierId,
      supplierName,
      orderQuantities,
      unitCosts,
      title,
      description,
      etaDate,
      locationId,
      currency,
      exchangeRate,
    }: {
      orderId: string;
      supplierId?: string | null;
      supplierName: string;
      orderQuantities: Record<string, number>;
      // BLU-571: per-line unit-cost overrides keyed by skuId (only changed lines)
      unitCosts?: Record<string, number>;
      title?: string;
      description?: string;
      etaDate?: string;
      locationId?: string | null;
      currency?: string;
      exchangeRate?: number;
    }) => {
      const resolvedLocationId = resolveLocationId(locationId);

      const payload: UpdateDraftInput = {
        orderId,
        locationId: resolvedLocationId,
        supplierId: supplierId ?? undefined,
        supplierName: supplierName || undefined,
        title: title ?? undefined,
        description: description ?? undefined,
        etaDate: etaDate || undefined,
        currency: currency ?? undefined,
        exchangeRate: exchangeRate ?? undefined,
        orderQuantities,
        unitCosts: unitCosts && Object.keys(unitCosts).length > 0 ? unitCosts : undefined,
      };

      await updateDraftMutation.mutateAsync(payload);
    },
    submitDraftOrder: async ({
      orderId,
      supplierId,
      supplierName,
      locationId
    }: {
      orderId: string;
      supplierId?: string | null;
      supplierName: string;
      locationId?: string | null;
    }) => {
      const resolvedLocationId = resolveLocationId(locationId);

      const payload: SubmitDraftInput = {
        orderId,
        locationId: resolvedLocationId,
        supplierId: supplierId ?? undefined,
        supplierName: supplierName || ''
      };

      await submitDraftMutation.mutateAsync(payload);
    },
    deleteDraftOrder: async (orderId: string, locationId?: string | null) => {
      const resolvedLocationId = resolveLocationId(locationId);

      const payload: DeleteDraftInput = {
        orderId,
        locationId: resolvedLocationId
      };

      await deleteDraftMutation.mutateAsync(payload);
    },
    isCreating: createOrderMutation.isPending,
    isCreatingDraft: createDraftMutation.isPending,
    isUpdatingDraft: updateDraftMutation.isPending,
    isSubmitting: submitDraftMutation.isPending,
    isDeleting: deleteDraftMutation.isPending
  };
}
